import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  alpha
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  buildVulnSummaryExport,
  buildVulnComplexExport,
  collectVulnExportTargets
} from '../../utils/socrVulnBoard'
import { fetchDtProjectDetails } from '../../api/dtProjects'
import { SEV } from './boardBadges'

const EXPORT_SEVERITY_OPTIONS = [
  { key: 'critical', label: 'Critical', color: SEV.critical },
  { key: 'high', label: 'High', color: SEV.high },
  { key: 'medium', label: 'Medium', color: SEV.medium },
  { key: 'low', label: 'Low', color: SEV.low }
]

const DEFAULT_EXPORT_SEVERITIES = ['critical', 'high', 'medium']

export const VulnExportDialog = ({
  open,
  onClose,
  clients = [],
  defaultSeverities = DEFAULT_EXPORT_SEVERITIES
}) => {
  const token = useSelector((state) => state.auth?.token)
  const [mode, setMode] = useState('simple')
  const [fileFormat, setFileFormat] = useState('txt')
  const [severities, setSeverities] = useState(() => new Set(defaultSeverities))
  const [includeVersion, setIncludeVersion] = useState(true)
  const [copied, setCopied] = useState(false)
  const [detailsByVersionId, setDetailsByVersionId] = useState({})
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [loadProgress, setLoadProgress] = useState({ done: 0, total: 0 })
  const [loadError, setLoadError] = useState(null)
  const fetchGenRef = useRef(0)
  const cacheKeyRef = useRef('')
  const wasOpenRef = useRef(false)

  const targets = useMemo(() => collectVulnExportTargets(clients), [clients])
  const targetIds = useMemo(() => targets.map((t) => t.versionId).sort().join(','), [targets])

  // Reset form only when the dialog opens (not on every parent re-render)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSeverities(new Set(defaultSeverities))
      setIncludeVersion(true)
      setCopied(false)
      setMode('simple')
      setFileFormat('txt')
      setLoadError(null)
      setDetailsByVersionId({})
      cacheKeyRef.current = ''
    }
    wasOpenRef.current = open
  }, [open, defaultSeverities])

  useEffect(() => {
    if (!open || mode !== 'complex') return undefined
    if (!token) {
      setLoadError('Not signed in')
      return undefined
    }
    if (!targets.length) {
      setDetailsByVersionId({})
      setLoadingDetails(false)
      setLoadError(null)
      return undefined
    }

    if (cacheKeyRef.current === targetIds) {
      setLoadingDetails(false)
      return undefined
    }

    const gen = ++fetchGenRef.current
    let cancelled = false
    setLoadingDetails(true)
    setLoadError(null)
    setLoadProgress({ done: 0, total: targets.length })

    fetchDtProjectDetails(
      token,
      targets.map((t) => t.versionId),
      {
        concurrency: 4,
        onProgress: (p) => {
          if (!cancelled && fetchGenRef.current === gen) setLoadProgress(p)
        }
      }
    )
      .then((map) => {
        if (cancelled || fetchGenRef.current !== gen) return
        setDetailsByVersionId(map)
        cacheKeyRef.current = targetIds
        const missing = targets.filter((t) => !map[t.versionId]).length
        if (missing) {
          setLoadError(`Loaded ${Object.keys(map).length}/${targets.length} projects (${missing} failed)`)
        } else {
          setLoadError(null)
        }
      })
      .catch((err) => {
        if (cancelled || fetchGenRef.current !== gen) return
        setLoadError(err?.message || 'Failed to load finding details')
        setDetailsByVersionId({})
      })
      .finally(() => {
        if (!cancelled && fetchGenRef.current === gen) setLoadingDetails(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, mode, token, targets, targetIds])

  useEffect(() => {
    cacheKeyRef.current = ''
  }, [targetIds])

  const severityList = useMemo(() => [...severities], [severities])

  const preview = useMemo(() => {
    if (mode === 'simple') {
      return buildVulnSummaryExport(clients, {
        severities: severityList,
        includeVersion
      })
    }
    if (loadingDetails) return ''
    return buildVulnComplexExport(targets, detailsByVersionId, {
      severities: severityList,
      includeVersion,
      format: fileFormat === 'txt' ? 'txt' : 'md'
    })
  }, [
    mode,
    clients,
    targets,
    detailsByVersionId,
    severityList,
    includeVersion,
    loadingDetails,
    fileFormat
  ])

  const toggleSeverity = (key) => {
    setSeverities((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleCopy = async () => {
    if (!preview) return
    try {
      await navigator.clipboard.writeText(preview)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch (_) {
      setCopied(false)
    }
  }

  const handleDownload = () => {
    if (!preview) return
    const ext = mode === 'complex' && fileFormat === 'md' ? 'md' : 'txt'
    const mime = ext === 'md' ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8'
    const blob = new Blob([preview], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `vuln-${mode}-${new Date().toISOString().slice(0, 10)}.${ext}`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const busy = mode === 'complex' && loadingDetails
  const canExport = Boolean(preview) && !busy
  const downloadExt = mode === 'complex' && fileFormat === 'md' ? 'md' : 'txt'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={mode === 'complex' ? 'md' : 'sm'}
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `1px solid ${alpha('#fff', 0.12)}`,
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        Export vulnerability summary
        <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
          {clients.length} client{clients.length === 1 ? '' : 's'} selected
          {mode === 'complex' && targets.length
            ? ` · ${targets.length} DT project${targets.length === 1 ? '' : 's'}`
            : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 0.75
          }}
        >
          Summary type
        </Typography>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={mode}
          onChange={(_, next) => {
            if (!next) return
            setMode(next)
            setFileFormat(next === 'complex' ? 'md' : 'txt')
          }}
          sx={{ mb: 1.75 }}
        >
          <ToggleButton value='simple' sx={{ px: 1.5, fontWeight: 800, textTransform: 'none' }}>
            Simple
          </ToggleButton>
          <ToggleButton value='complex' sx={{ px: 1.5, fontWeight: 800, textTransform: 'none' }}>
            Complex
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant='caption' sx={{ display: 'block', color: alpha('#fff', 0.55), mb: 1.5, mt: -1 }}>
          {mode === 'simple'
            ? 'Short per-client rollup (counts + project count).'
            : 'Per-project summary with CVE, package upgrade path, and description. Loads finding details from Dependency-Track.'}
        </Typography>

        {mode === 'complex'
          ? (
            <>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mb: 0.75
                }}
              >
                File format
              </Typography>
              <ToggleButtonGroup
                exclusive
                size='small'
                value={fileFormat}
                onChange={(_, next) => {
                  if (next) setFileFormat(next)
                }}
                sx={{ mb: 1.75 }}
              >
                <ToggleButton value='md' sx={{ px: 1.5, fontWeight: 800, textTransform: 'none' }}>
                  Markdown (.md)
                </ToggleButton>
                <ToggleButton value='txt' sx={{ px: 1.5, fontWeight: 800, textTransform: 'none' }}>
                  Plain text (.txt)
                </ToggleButton>
              </ToggleButtonGroup>
            </>
            )
          : null}

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 0.75
          }}
        >
          Severities to include
        </Typography>
        <FormGroup row sx={{ mb: 1.5, gap: 0.5 }}>
          {EXPORT_SEVERITY_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.key}
              control={(
                <Checkbox
                  size='small'
                  checked={severities.has(opt.key)}
                  onChange={() => toggleSeverity(opt.key)}
                  sx={{
                    color: alpha(opt.color, 0.7),
                    '&.Mui-checked': { color: opt.color }
                  }}
                />
              )}
              label={opt.label}
              sx={{ mr: 1.5, '& .MuiFormControlLabel-label': { fontWeight: 700, fontSize: '0.85rem' } }}
            />
          ))}
        </FormGroup>

        <FormControlLabel
          control={(
            <Checkbox
              size='small'
              checked={includeVersion}
              onChange={(e) => setIncludeVersion(e.target.checked)}
            />
          )}
          label={mode === 'complex'
            ? 'Include Version line (BOM version / import date)'
            : 'Include Version line when BOM import date is available'}
          sx={{ mb: 1.5, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.82rem' } }}
        />

        {loadError && mode === 'complex'
          ? (
            <Typography variant='caption' sx={{ display: 'block', color: alpha('#ffb74d', 0.95), mb: 1 }}>
              {loadError}
            </Typography>
            )
          : null}

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 0.75
          }}
        >
          Preview
        </Typography>
        <Box
          component='pre'
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1.5,
            border: `1px solid ${alpha('#fff', 0.12)}`,
            backgroundColor: alpha('#000', 0.35),
            color: alpha('#fff', 0.9),
            fontSize: '0.8rem',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: mode === 'complex' ? 420 : 280,
            overflow: 'auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            position: 'relative',
            minHeight: busy ? 120 : undefined
          }}
        >
          {busy
            ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: alpha('#fff', 0.7) }}>
                <CircularProgress size={18} />
                <span>
                  Loading findings… {loadProgress.done}/{loadProgress.total || targets.length}
                </span>
              </Box>
              )
            : (preview || 'Nothing to export.')}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant='outlined'
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          disabled={!canExport}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant='contained'
          startIcon={<FileDownloadIcon />}
          onClick={handleDownload}
          disabled={!canExport}
        >
          {`Download .${downloadExt}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
